import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronsUpDown, Plus, Check, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CreateWorkspaceDialog } from "@/components/CreateWorkspaceDialog";
import type { Workspace } from "@/hooks/use-workspace";

interface WorkspaceSwitcherProps {
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  collapsed: boolean;
  onSwitch: (id: string) => void;
  onCreate: (name: string) => Promise<string | null>;
}

export function WorkspaceSwitcher({
  workspaces,
  activeWorkspace,
  collapsed,
  onSwitch,
  onCreate,
}: WorkspaceSwitcherProps) {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const isOwner = activeWorkspace?.role === "owner";

  const handleCreate = async (name: string) => {
    await onCreate(name);
    setCreateOpen(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md hover:bg-sidebar-accent transition-colors text-left">
            <div className="w-6 h-6 rounded bg-accent/20 flex items-center justify-center shrink-0">
              <span className="font-display text-2xs font-bold text-accent">
                {activeWorkspace?.name?.charAt(0)?.toUpperCase() || "W"}
              </span>
            </div>
            {!collapsed && (
              <>
                <span className="text-xs font-medium text-sidebar-foreground truncate flex-1">
                  {activeWorkspace?.name || "Select Workspace"}
                </span>
                {activeWorkspace?.is_superadmin && (
                  <span className="font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-accent/20 text-accent shrink-0">
                    SA
                  </span>
                )}
                <ChevronsUpDown className="w-3 h-3 text-muted-foreground shrink-0" />
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <div className="px-2 py-1.5">
            <p className="font-mono text-2xs uppercase tracking-widest text-muted-foreground">
              Workspaces
            </p>
          </div>
          {workspaces.map((ws) => (
            <DropdownMenuItem
              key={ws.id}
              onClick={() => onSwitch(ws.id)}
              className="flex items-center gap-2"
            >
              <div className="w-5 h-5 rounded bg-accent/20 flex items-center justify-center shrink-0">
                <span className="font-display text-3xs font-bold text-accent">
                  {ws.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="text-sm truncate flex-1">{ws.name}</span>
              {ws.is_superadmin && (
                <span className="font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-accent/20 text-accent shrink-0">
                  SA
                </span>
              )}
              {ws.id === activeWorkspace?.id && (
                <Check className="w-3.5 h-3.5 text-accent shrink-0" />
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          {isOwner && (
            <DropdownMenuItem onClick={() => navigate(`/workspace/${activeWorkspace?.id}`)}>
              <Users className="w-3.5 h-3.5 mr-2" />
              <span className="text-sm">Manage Members</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setCreateOpen(true)}>
            <Plus className="w-3.5 h-3.5 mr-2" />
            <span className="text-sm">Create Workspace</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateWorkspaceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={handleCreate}
      />
    </>
  );
}
